import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2, UserRoundPlus } from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";

import { APIError } from "@/adapters/api";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { usePermissions } from "@/hooks/use-permissions.ts";
import { useSaveWorker } from "../../hooks/use-save-worker";
import {
	type WorkerFormValues,
	workerSchema,
} from "../../schemas/worker-schema";
import type { Worker } from "../../types";

const API_FIELDS: Record<string, keyof WorkerFormValues> = {
	name: "name",
	job_title: "jobTitle",
};

export function WorkerDialog({
	open,
	onOpenChange,
	worker,
}: {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	worker?: Worker;
}) {
	const saveWorker = useSaveWorker();
	const [formError, setFormError] = useState<string | null>(null);
	const isEdit = worker != null;

	const {
		register,
		handleSubmit,
		reset,
		setError,
		formState: { errors },
	} = useForm<WorkerFormValues>({
		resolver: zodResolver(workerSchema),
		defaultValues: { name: "", jobTitle: "" },
	});

	useEffect(() => {
		if (!open) return;
		reset({ name: worker?.name ?? "", jobTitle: worker?.jobTitle ?? "" });
		setFormError(null);
	}, [open, worker, reset]);

	async function onSubmit(values: WorkerFormValues) {
		setFormError(null);
		try {
			if (worker) {
				await saveWorker.mutateAsync({
					workerId: worker.id,
					name: values.name,
					jobTitle: values.jobTitle,
				});
				toast.success("Pessoa atualizada");
			} else {
				await saveWorker.mutateAsync({
					name: values.name,
					jobTitle: values.jobTitle,
				});
				toast.success("Pessoa cadastrada");
			}
			onOpenChange(false);
		} catch (err) {
			if (err instanceof APIError) {
				let matchedField = false;
				for (const [field, message] of Object.entries(err.violations)) {
					const formField = API_FIELDS[field];
					if (formField) {
						setError(formField, { message });
						matchedField = true;
					}
				}
				if (!matchedField) {
					setFormError(err.message || "Não foi possível salvar a pessoa.");
				}
				return;
			}
			setFormError("Não foi possível salvar a pessoa. Tente novamente.");
		}
	}

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="worker-dialog">
				<DialogHeader>
					<DialogTitle>
						{isEdit ? "Editar pessoa" : "Cadastrar pessoa"}
					</DialogTitle>
					<DialogDescription>
						{isEdit
							? "Atualize o nome e a função desta pessoa na equipe."
							: "Adicione alguém à equipe deste canteiro sem precisar de login. Você pode convidar para o app depois."}
					</DialogDescription>
				</DialogHeader>

				<form
					id="worker-form"
					className="worker-dialog__form"
					onSubmit={handleSubmit(onSubmit)}
					noValidate
				>
					<div className="form-group">
						<Label htmlFor="worker-name">Nome</Label>
						<Input
							id="worker-name"
							placeholder="Ex.: José da Silva"
							autoComplete="off"
							aria-invalid={!!errors.name}
							{...register("name")}
						/>
						{errors.name && <p className="form-error">{errors.name.message}</p>}
					</div>

					<div className="form-group">
						<Label htmlFor="worker-job-title">Função (opcional)</Label>
						<Input
							id="worker-job-title"
							placeholder="Ex.: Pedreiro"
							autoComplete="off"
							aria-invalid={!!errors.jobTitle}
							{...register("jobTitle")}
						/>
						{errors.jobTitle && (
							<p className="form-error">{errors.jobTitle.message}</p>
						)}
					</div>

					{formError && (
						<p role="alert" className="form-error-alert">
							{formError}
						</p>
					)}
				</form>

				<DialogFooter>
					<Button
						type="button"
						variant="ghost"
						onClick={() => onOpenChange(false)}
						disabled={saveWorker.isPending}
					>
						Cancelar
					</Button>
					<Button
						type="submit"
						form="worker-form"
						disabled={saveWorker.isPending}
					>
						{saveWorker.isPending && (
							<Loader2 className="worker-dialog__spinner" />
						)}
						{isEdit ? "Salvar" : "Cadastrar"}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}

export function NewWorkerButton() {
	const [open, setOpen] = useState(false);
	const { has } = usePermissions();

	if (!has("workers:manage")) return null;

	return (
		<>
			<Button variant="outline" onClick={() => setOpen(true)}>
				<UserRoundPlus /> Cadastrar pessoa
			</Button>
			<WorkerDialog open={open} onOpenChange={setOpen} />
		</>
	);
}
